const CHECK_MS = 500;
const STUCK_AUTO_RECOVER_MS = 2000;
const AUDIO_LEVEL_THRESHOLD = 0.05;
const AUDIO_LEVEL_DETECTION_MS = 1000;
const MAX_RECOVERY_ATTEMPTS = 3;
const RECOVERY_COOLDOWN_MS = 1500;
const IS_STARTING_STUCK_MS = 3000;

interface RecoveryRefs {
  lastListeningAt: number | null;
  highAudioLevelStart: number | null;
  lastRecoveryAttempt: number;
  recoveryAttemptCount: number;
  isRecovering: boolean;
  isStartingBeginTime: number | null;
  isStartingStuck: number | null;
}

export function createListeningRecoveryRefs(): RecoveryRefs {
  return {
    lastListeningAt: null,
    highAudioLevelStart: null,
    lastRecoveryAttempt: 0,
    recoveryAttemptCount: 0,
    isRecovering: false,
    isStartingBeginTime: null,
    isStartingStuck: null,
  };
}

export function primeShouldBeListeningTimer(
  refs: RecoveryRefs,
  shouldBeListening: boolean,
  isListening: boolean
) {
  if (shouldBeListening && !isListening) {
    if (refs.lastListeningAt === null) {
      refs.lastListeningAt = Date.now();
      refs.recoveryAttemptCount = 0;
    }
  } else if (!shouldBeListening) {
    refs.lastListeningAt = null;
    refs.highAudioLevelStart = null;
    refs.recoveryAttemptCount = 0;
    refs.isRecovering = false;
    refs.isStartingBeginTime = null;
  }
}

export function resetTimerOnListeningStart(
  refs: RecoveryRefs,
  isListening: boolean,
  shouldBeListening: boolean
) {
  if (isListening && shouldBeListening) {
    refs.lastListeningAt = Date.now();
    refs.highAudioLevelStart = null;
    refs.recoveryAttemptCount = 0;
    refs.isRecovering = false;
  }
}

export function trackIsStartingBegin(
  refs: RecoveryRefs,
  isStarting: boolean,
  shouldBeListening: boolean,
  isListening: boolean
) {
  if (isStarting && shouldBeListening && refs.isStartingBeginTime === null) {
    refs.isStartingBeginTime = Date.now();
  } else if (!isStarting && refs.isStartingBeginTime !== null) {
    if (!isListening && refs.lastListeningAt === null) {
      refs.lastListeningAt = Date.now();
    }
    refs.isStartingBeginTime = null;
  }
}

export function resetForInactiveSession(refs: RecoveryRefs) {
  refs.lastListeningAt = null;
  refs.highAudioLevelStart = null;
  refs.recoveryAttemptCount = 0;
  refs.isStartingStuck = null;
}

interface AttemptRecoveryDeps {
  isStarting: boolean;
  isListening: boolean;
  startListening: () => Promise<void>;
  stopListening: (reset?: boolean) => Promise<void>;
}

export async function attemptRecovery(
  refs: RecoveryRefs,
  {
    isStarting,
    isListening,
    startListening,
    stopListening,
  }: AttemptRecoveryDeps
) {
  if (refs.isRecovering) {
    return;
  }

  const now = Date.now();
  const timeSinceLastAttempt = now - refs.lastRecoveryAttempt;

  if (timeSinceLastAttempt < RECOVERY_COOLDOWN_MS) {
    return;
  }

  if (refs.recoveryAttemptCount >= MAX_RECOVERY_ATTEMPTS) {
    const backoffMs =
      RECOVERY_COOLDOWN_MS *
      Math.pow(2, refs.recoveryAttemptCount - MAX_RECOVERY_ATTEMPTS);
    if (timeSinceLastAttempt < backoffMs) {
      return;
    }
    refs.recoveryAttemptCount = 0;
  }

  refs.isRecovering = true;
  refs.lastRecoveryAttempt = now;
  refs.recoveryAttemptCount += 1;

  try {
    if (isStarting && !isListening) {
      await stopListening(true);
      await new Promise((resolve) => setTimeout(resolve, 300));
    } else {
      await stopListening(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    await startListening();

    refs.recoveryAttemptCount = 0;
  } catch {
    // Silently handle errors - recovery will retry automatically
    // Multiple recovery mechanisms ensure we eventually recover
  } finally {
    setTimeout(() => {
      refs.isRecovering = false;
    }, 500);
  }
}

interface RecoveryIntervalInputs {
  shouldBeListening: boolean;
  isInActiveVoiceSession: boolean;
  isListening: boolean;
  isStarting: boolean;
  audioLevel: number;
  runAttemptRecovery: () => void;
}

export function runRecoveryIntervalTick(
  refs: RecoveryRefs,
  {
    shouldBeListening,
    isInActiveVoiceSession,
    isListening,
    isStarting,
    audioLevel,
    runAttemptRecovery,
  }: RecoveryIntervalInputs
) {
  const currentlyShouldBeListening =
    shouldBeListening || isInActiveVoiceSession;
  if (!currentlyShouldBeListening) {
    return;
  }

  if (isListening) {
    refs.lastListeningAt = Date.now();
    refs.highAudioLevelStart = null;
    refs.recoveryAttemptCount = 0;
    refs.isStartingStuck = null;
    return;
  }

  if (isStarting && !isListening) {
    if (refs.isStartingStuck === null) {
      refs.isStartingStuck = Date.now();
    } else {
      const stuckStartingMs = Date.now() - refs.isStartingStuck;
      if (stuckStartingMs > IS_STARTING_STUCK_MS) {
        runAttemptRecovery();
        refs.isStartingStuck = null;
        return;
      }
    }
    return;
  } else {
    refs.isStartingStuck = null;
  }

  if (audioLevel > AUDIO_LEVEL_THRESHOLD) {
    if (refs.highAudioLevelStart === null) {
      refs.highAudioLevelStart = Date.now();
    } else {
      const highAudioDuration = Date.now() - refs.highAudioLevelStart;
      if (highAudioDuration >= AUDIO_LEVEL_DETECTION_MS) {
        runAttemptRecovery();
        refs.lastListeningAt = Date.now();
        refs.highAudioLevelStart = null;
        return;
      }
    }
  } else {
    refs.highAudioLevelStart = null;
  }

  if (refs.lastListeningAt === null) {
    refs.lastListeningAt = Date.now();
    return;
  }

  const stuckMs = Date.now() - refs.lastListeningAt;
  if (stuckMs >= STUCK_AUTO_RECOVER_MS) {
    runAttemptRecovery();
    refs.lastListeningAt = Date.now();
    refs.highAudioLevelStart = null;
  }
}

export function createRecoveryInterval(
  tick: () => void
): ReturnType<typeof setInterval> {
  return setInterval(tick, CHECK_MS);
}
